import { useState } from "react";
import { Button, Menu, MenuItem, styled } from "~/common/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

import { useBooleanState } from "~/common/hooks/useBooleanState";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { canSeeWishlistButton } from "~/utils/features";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { ReportType } from "./DownloadReportButton/api/downloadCartReport";
import { EnterDesignationBtn } from "./EnterDesignationButton";
import BulkATCButton from "./BulkATCButton";
import AddCartToWishlistBtn from "./AddCartToWishlistBtn";
import ClearCartButton from "./ClearCartButton";
import { DownloadReportButton } from "./DownloadReportButton/DownloadReportButton";
import { LoadAllPnAButton } from "./LoadAllPnAButton";
import { SwitchProductVersions } from "./SwitchProductVersions";
import { ChoosePriceReferenceForCart } from "./ChoosePriceReferenceForCart";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";

export const QuickActionMenuItem = styled(MenuItem)(({ theme }) => ({
  minWidth: 300,
  padding: 0,
  "& .scha__link": {
    color: `${theme.palette.text.primary} !important`,
    padding: `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(1)} ${theme.spacing(2)} !important`,
    gap: theme.spacing(1),
    width: "100%",
  },
}));

interface Props {
  disableReportDownload: boolean;
  switchProductVersionForCart: () => void;
  updatePriceReferenceForCart: (priceReference: string) => void;
}

export const CartQuickActionsButton = ({
  disableReportDownload,
  switchProductVersionForCart,
  updatePriceReferenceForCart,
}: Props) => {
  const { t } = useTranslation();
  const canBulkUpload = userHasPermission(Permission.BULK_UPLOAD);
  const hideSwitchProductVersions = isPhaseOut2Enabled();

  const { val: showActionBtnMenu, setFalse: closeActionBtnMenu, toggle: openActionBtnMenu } = useBooleanState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenActionBtnMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    openActionBtnMenu();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionBtnMenu = () => {
    closeActionBtnMenu();
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        color="secondary"
        onClick={handleOpenActionBtnMenu}
        data-test-id={"btn-actions"}
        endIcon={<ExpandMoreIcon sx={{ marginLeft: "8px", transform: showActionBtnMenu ? "rotate(180deg)" : "" }} />}
      >
        {t("cartPage.btn.actions")}
      </Button>
      <Menu
        keepMounted
        open={showActionBtnMenu}
        anchorEl={anchorEl}
        onClose={handleCloseActionBtnMenu}
        onClick={handleCloseActionBtnMenu}
        {...MENU_POS_RIGHT}
        sx={{ marginTop: "10px" }}
        slotProps={{ paper: { sx: { borderRadius: "12px", border: `1px solid ${C_OUTLINE_VARIANT}` } } }}
      >
        <QuickActionMenuItem>
          <EnterDesignationBtn />
        </QuickActionMenuItem>
        {canBulkUpload && (
          <QuickActionMenuItem>
            <BulkATCButton isB2BCart={true} />
          </QuickActionMenuItem>
        )}
        {canSeeWishlistButton() && (
          <QuickActionMenuItem>
            <AddCartToWishlistBtn isB2BCart={true} />
          </QuickActionMenuItem>
        )}
        <QuickActionMenuItem>
          <ClearCartButton isB2BCart={true} />
        </QuickActionMenuItem>
        <QuickActionMenuItem>
          <DownloadReportButton
            isB2BCart={true}
            type={ReportType.XLSX}
            disabled={disableReportDownload}
          />
        </QuickActionMenuItem>
        <LoadAllPnAButton />
        {!hideSwitchProductVersions && (
          <SwitchProductVersions
            isB2BCart={true}
            switchProductVersion={switchProductVersionForCart}
          />
        )}
        <ChoosePriceReferenceForCart
          isB2BCart={true}
          updatePriceReference={updatePriceReferenceForCart}
        />
      </Menu>
    </>
  );
};
